/**
 * Saved products (wishlist).
 *
 * COST: this screen adds NO Firestore reads. The saved ids come from the
 * dealer's own user doc, which AuthContext already streams, and the product
 * details are resolved from the per-category catalogs already cached on the
 * device by the Home screen. A category the dealer has never opened is fetched
 * from the edge catalog (Cloudflare KV) — the same free, cached path Home uses,
 * never a per-product Firestore read.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Product } from '@mitang/shared';
import { AppBar } from '../../components/AppBar';
import { Spinner } from '../../components/Spinner';
import { ProductCard } from '../product/ProductCard';
import { useWishlist } from '../../hooks/useWishlist';
import { useScrollRestore } from '../../hooks/useScrollRestore';
import { cacheKeys, readCache, writeCache } from '../../lib/localCache';
import { fetchProductsByIds } from '../../lib/repo';
import { ROUTES } from '../../routes/paths';
import { useOpenProduct } from '../../routes/productOverlay';

interface CachedCatalog {
  version?: number;
  freshAt?: number;
  products?: Product[];
}

/** Products resolved for the saved list, so revisits cost nothing. */
const SAVED_CACHE = 'saved.products';

export function SavedScreen(): JSX.Element {
  const navigate = useNavigate();
  const wishlist = useWishlist();
  const savedIds = useMemo(() => new Set(wishlist.ids), [wishlist.ids]);

  const [byId, setById] = useState<Map<string, Product>>(new Map());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (wishlist.ids.length === 0) {
      setById(new Map());
      setLoading(false);
      return;
    }

    // 1. Sweep every category catalog already cached on the device, plus the
    //    resolved-product cache from a previous visit. Free, instant, offline.
    const found = new Map<string, Product>();
    const sweep = (products: Product[] | undefined): void => {
      for (const p of products ?? []) if (savedIds.has(p.id)) found.set(p.id, p);
    };
    sweep(readCache<Product[]>(SAVED_CACHE) ?? undefined);
    for (const category of cacheKeys('catalog.')) {
      sweep(readCache<CachedCatalog>(`catalog.${category}`)?.products);
    }
    setById(new Map(found));

    const missing = wishlist.ids.filter((id) => !found.has(id));
    if (missing.length === 0) {
      setLoading(false);
      return;
    }

    // 2. Only the stragglers are fetched, by id — bounded by how many products
    //    the dealer saved, never by the size of the catalogue.
    setLoading(true);
    void fetchProductsByIds(missing)
      .then((products) => {
        if (cancelled) return;
        const merged = new Map(found);
        for (const p of products) merged.set(p.id, p);
        setById(merged);
        // Remember them so the next visit resolves for free.
        writeCache(SAVED_CACHE, [...merged.values()]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [savedIds, wishlist.ids]);

  // Keep the dealer's own order (newest saved first), dropping ids whose product
  // has since left the sheet.
  const products = useMemo(
    () => wishlist.ids.map((id) => byId.get(id)).filter((p): p is Product => !!p),
    [byId, wishlist.ids],
  );
  const stale = wishlist.ids.length - products.length;

  // Coming back from a product returns to the same spot, tapped card in view.
  const listRef = useRef<HTMLDivElement>(null);
  const rememberAnchor = useScrollRestore('saved', listRef, products.length > 0);
  const openProduct = useOpenProduct();

  return (
    <div className="flex min-h-full flex-1 flex-col bg-ground">
      <AppBar title="Saved products" />

      {wishlist.ids.length === 0 ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 px-10 text-center">
          <span className="text-4xl" aria-hidden>
            ♡
          </span>
          <p className="text-[15px] font-bold text-ink">Nothing saved yet</p>
          <p className="text-[13px] leading-relaxed text-slate-500">
            Tap the heart on any product to keep it here — handy for the models you quote most.
          </p>
          <button type="button" className="btn-secondary mt-2" onClick={() => navigate(ROUTES.home)}>
            Browse products
          </button>
        </div>
      ) : (
        <>
          <p className="px-5 pt-2 text-[11.5px] font-semibold text-slate-400">
            {products.length} {products.length === 1 ? 'product' : 'products'} saved
            {stale > 0 ? ` · ${stale} no longer in the price list` : ''}
          </p>
          {loading && products.length === 0 ? (
            <Spinner label="Loading saved products…" />
          ) : (
            <div ref={listRef} className="flex-1 overflow-y-auto px-4 pb-6 pt-2">
              {products.map((p) => (
                <div key={p.id} className="pb-3" data-scroll-anchor={p.id}>
                  <ProductCard
                    product={p}
                    recentlyUpdated={false}
                    onOpen={(prod) => {
                      rememberAnchor(prod.id);
                      openProduct(prod.id);
                    }}
                    saved={wishlist.isSaved(p.id)}
                    onToggleSaved={wishlist.toggle}
                  />
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
